import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Briefcase, DollarSign, Edit, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { InlineEdit } from "@/components/InlineEdit";
import { RecordPanel } from "@/components/record/RecordFrame";
import { formatDealValue } from "@/lib/formatDealValue";
import type { CompanyData } from "../companyTypes";

/** What enrichment and the team know about the business: details, services, AI insight, sale readiness, ownership. */
export function ProspectSnapshot({ company, saveCompanyFieldAsync }: {
  company: CompanyData; saveCompanyFieldAsync: (field: string, value: unknown) => Promise<unknown>;
}) {
  const queryClient = useQueryClient();
  const id = company.id;
  const [editingThesis, setEditingThesis] = useState(false);
  const [thesisText, setThesisText] = useState("");
  return (
    <RecordPanel title="Company snapshot">
      <div className="grid gap-4">
                  <section>
                    <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-4">Company Details</h3>
                    <div className="grid grid-cols-2 gap-x-8 gap-y-4">
                      <div className="space-y-1">
                        <label className="text-xs font-medium text-muted-foreground block">Industry</label>
                        <InlineEdit label="Industry" value={company.industry} placeholder="Set industry" addLabel="Add" onSave={(v) => saveCompanyFieldAsync("industry", v)} />
                      </div>
                      <div className="space-y-1">
                        <label className="text-xs font-medium text-muted-foreground block">Location</label>
                        <InlineEdit label="Location" value={company.headquarters} placeholder="City, State" addLabel="Add" onSave={(v) => saveCompanyFieldAsync("headquarters", v)} />
                      </div>
                      <div className="space-y-1">
                        <label className="text-xs font-medium text-muted-foreground block">Est. Revenue</label>
                        <InlineEdit label="Revenue" value={company.revenueRange} placeholder="e.g. $10M" addLabel="Add" onSave={(v) => saveCompanyFieldAsync("revenueRange", formatDealValue(v))} />
                        {company.enrichmentData?.financials?.ebitdaMarginContext && (
                          <p className="text-[11px] text-muted-foreground mt-1">{company.enrichmentData.financials.ebitdaMarginContext}</p>
                        )}
                      </div>
                      <div className="space-y-1">
                        <label className="text-xs font-medium text-muted-foreground block">Est. Deal Value</label>
                        {company.enrichmentData?.estimatedDealValue ? (
                          <div>
                            <p className="text-sm font-medium">{company.enrichmentData.estimatedDealValue}</p>
                            <p className="text-xs text-muted-foreground">AI estimate</p>
                          </div>
                        ) : (
                          <InlineEdit label="Deal value" value={company.targetAmount?.toString()} placeholder="e.g. $5M-$10M" addLabel="Add" onSave={(v) => saveCompanyFieldAsync("targetAmount", formatDealValue(v))} />
                        )}
                      </div>
                      <div className="space-y-1">
                        <label className="text-xs font-medium text-muted-foreground block">Engagement Type</label>
                        <InlineEdit label="Engagement type" value={company.engagementType} type="select" options={["M&A", "Capital Raise", "Debt Placement", "Restructuring", "ESOP", "Other"]} addLabel="Add" onSave={(v) => saveCompanyFieldAsync("engagementType", v)} />
                      </div>
                      <div className="space-y-1">
                        <label className="text-xs font-medium text-muted-foreground block">Employees</label>
                        <InlineEdit label="Employees" value={company.employeeCount} placeholder="e.g. 85" addLabel="Add" onSave={(v) => saveCompanyFieldAsync("employeeCount", v)} />
                      </div>
                    </div>
                    {company.enrichmentData?.financials?.industryContext && (
                      <div className="mt-3 pt-3 border-t border-dashed">
                        <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground mb-1">Industry & Value Drivers</p>
                        <p className="text-xs text-muted-foreground">{company.enrichmentData.financials.industryContext}</p>
                      </div>
                    )}
                  </section>

                  {company.enrichmentData?.products && company.enrichmentData.products.length > 0 && (
                    <section>
                      <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3 flex items-center gap-1.5">
                        <Briefcase className="w-3.5 h-3.5" /> Services & Products
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        {company.enrichmentData.products.map((p: string, i: number) => (
                          <span key={i} className="px-3 py-1 bg-muted rounded-full text-sm">{p}</span>
                        ))}
                      </div>
                    </section>
                  )}
                  {(company.enrichmentData?.prospectThesis || company.enrichmentData?.estimatedDealValue) && (
                    <section className="rounded-md border border-amber-200 bg-amber-50/30 p-3 dark:border-amber-900 dark:bg-amber-950/40">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-xs font-semibold uppercase tracking-wide text-amber-700 flex items-center gap-1.5 dark:text-amber-400"><Sparkles className="w-3.5 h-3.5" /> AI Prospect Insight</h3>
                        {company.enrichmentData?.prospectThesis && !editingThesis && (
                          <Button variant="ghost" size="sm" className="h-6 min-h-0 text-xs" onClick={() => { setThesisText(company.enrichmentData?.prospectThesis || ""); setEditingThesis(true); }}><Edit className="w-3 h-3 mr-1" />Edit</Button>
                        )}
                      </div>
                      {company.enrichmentData?.prospectThesis && (
                        editingThesis ? (
                          <div className="space-y-2">
                            <textarea className="w-full min-h-[60px] p-3 border rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-amber-200 bg-background" value={thesisText} onChange={(e) => setThesisText(e.target.value)} />
                            <div className="flex gap-2 justify-end">
                              <Button variant="outline" size="sm" onClick={() => setEditingThesis(false)}>Cancel</Button>
                              <Button size="sm" onClick={async () => {
                                const r = await fetch(`/api/companies/${id}/update-enrichment-field`, { method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include", body: JSON.stringify({ field: "prospectThesis", value: thesisText }) });
                                if (!r.ok) { toast.error("Couldn't save the insight. Try again."); return; }
                                queryClient.invalidateQueries({ queryKey: ["company", id] });
                                setEditingThesis(false);
                                toast.success("Saved");
                              }}>Save</Button>
                            </div>
                          </div>
                        ) : (
                          <p className="text-sm whitespace-pre-wrap mb-3">{company.enrichmentData.prospectThesis}</p>
                        )
                      )}
                      {company.enrichmentData?.estimatedDealValue && (
                        <div className="flex items-center gap-2 text-sm pt-2 border-t border-amber-200/50 dark:border-amber-900">
                          <DollarSign className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400" />
                          <span className="font-medium">Estimated Deal Value:</span>
                          <span>{company.enrichmentData.estimatedDealValue}</span>
                        </div>
                      )}
                    </section>
                  )}

                    {/* Sale readiness signals, read-only. The High/Medium/Low score is not shown: no scores on record pages. */}
                    {company.enrichmentData?.saleReadiness && (
                      <section>
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">AI Sale Readiness</span>
                        </div>
                        {company.enrichmentData.saleReadiness.signals && company.enrichmentData.saleReadiness.signals.length > 0 && (
                          <ul className="space-y-1 mb-2">
                            {company.enrichmentData.saleReadiness.signals.map((signal: string, i: number) => (
                              <li key={i} className="text-xs text-muted-foreground flex items-start gap-1.5">
                                <span className="text-amber-500 mt-0.5">&#8226;</span>
                                <span>{signal}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                        {company.enrichmentData.saleReadiness.notes && (
                          <p className="text-xs text-muted-foreground italic">{company.enrichmentData.saleReadiness.notes}</p>
                        )}
                      </section>
                    )}

                  {/* Ownership — from AI research */}
                  {company.enrichmentData?.ownership && (company.enrichmentData.ownership.owner || company.enrichmentData.ownership.ownershipNotes) && (
                    <section>
                      <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3">Ownership</h3>
                      <div className="space-y-2.5 text-sm">
                        {company.enrichmentData.ownership.owner && (
                          <div>
                            <span className="font-medium">{company.enrichmentData.ownership.owner}</span>
                            {company.enrichmentData.ownership.ownerType && company.enrichmentData.ownership.ownerType !== 'Unknown' && (
                              <span className="ml-2 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-blue-50 text-blue-600 dark:bg-blue-950/40 dark:text-blue-400">{company.enrichmentData.ownership.ownerType}</span>
                            )}
                          </div>
                        )}
                        {company.enrichmentData.ownership.ownerBackground && (
                          <p className="text-xs text-muted-foreground">{company.enrichmentData.ownership.ownerBackground}</p>
                        )}
                        {company.enrichmentData.ownership.isFamilyOwned && (
                          <div className="flex items-center gap-1.5">
                            <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-amber-50 text-amber-600 dark:bg-amber-950/40 dark:text-amber-400">Family-Owned{company.enrichmentData.ownership.isMultiGenerational ? ' (Multi-Gen)' : ''}</span>
                          </div>
                        )}
                        {company.enrichmentData.ownership.familyDetails && (
                          <p className="text-xs text-muted-foreground">{company.enrichmentData.ownership.familyDetails}</p>
                        )}
                        {company.enrichmentData.ownership.ceo && company.enrichmentData.ownership.ceo !== company.enrichmentData.ownership.owner && (
                          <div className="flex justify-between text-xs"><span className="text-muted-foreground">CEO</span><span>{company.enrichmentData.ownership.ceo}</span></div>
                        )}
                        {company.enrichmentData.ownership.ownershipNotes && (
                          <p className="text-xs text-muted-foreground">{company.enrichmentData.ownership.ownershipNotes}</p>
                        )}
                        {company.enrichmentData.ownership.howToFind && (
                          <div className="mt-2 p-2 bg-blue-50/50 rounded text-xs text-blue-700 dark:bg-blue-950/40 dark:text-blue-400">
                            <span className="font-medium">Next steps: </span>{company.enrichmentData.ownership.howToFind}
                          </div>
                        )}
                      </div>
                    </section>
                  )}
      </div>
    </RecordPanel>
  );
}
